import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { AuthContextType, AuthConfig, LoginResult, User } from "./types";

const AuthContext = createContext<AuthContextType | null>(null);
export const AuthConfigContext = createContext<AuthConfig | null>(null);

// API response types (snake_case to match C# SnakeCaseLower serializer)
interface MeResponse {
  id: string;
  email: string;
  display_name?: string;
  is_active: boolean;
  roles?: string[];
  product_roles?: Array<{ product: string; role: string }>;
}

interface AuthApiResponse {
  user: {
    id: string;
    email: string;
    display_name?: string;
  };
}

function getAuthUrl(config: AuthConfig): string {
  const authUrl = config.authUrl ?? config.passportUrl;
  if (!authUrl) {
    throw new Error("AuthProvider requires config.authUrl or config.passportUrl");
  }
  return authUrl;
}

async function loadCurrentUser(authUrl: string): Promise<User | null> {
  const res = await fetch(`${authUrl}/auth/me`, {
    credentials: "include",
  });
  if (!res.ok) {
    return null;
  }

  const data: MeResponse = await res.json();
  const productRoles = data.product_roles ?? [];
  const roles = data.roles ?? [...new Set(productRoles.map((r) => r.role))];

  return {
    id: data.id,
    email: data.email,
    displayName: data.display_name,
    productRoles,
    roles,
    isAuthenticated: data.is_active,
  };
}

export function AuthProvider({
  config,
  children,
}: {
  config: AuthConfig;
  children: React.ReactNode;
}) {
  const authUrl = getAuthUrl(config);

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setUser(await loadCurrentUser(authUrl));
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [authUrl]);

  const loginWithPassport = useCallback(() => {
    // clientVariant is required (see types.ts). Always send &client=<variant> so the API's
    // OidcCallbackController picks the matching AUTH_OIDC_* / AUTH_OIDC_ADMIN_* key set;
    // it returns 400 missing_client_variant if the param is absent.
    //
    // After successful auth the API redirects to the configured PostLoginUrl for that variant.
    // There is no client-supplied returnUrl handoff — the post-login URL is fully managed in
    // Doppler.
    window.location.href = `${authUrl}/auth/sso/begin?client=${encodeURIComponent(config.clientVariant)}`;
  }, [authUrl, config.clientVariant]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      setError(null);
      setIsLoading(true);
      try {
        const res = await fetch(`${authUrl}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        });
        // 202 = MFA required: the password step passed but no session cookie was set. Return the pending token
        // for the caller's second step — do NOT setUser (loadCurrentUser would 401, stranding the page).
        if (res.status === 202) {
          const body = (await res.json().catch(() => ({}))) as { mfa_token?: string };
          if (!body.mfa_token) throw new Error("Login failed");
          return { mfaRequired: true, mfaToken: body.mfa_token };
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const typedBody = body as { error?: string; message?: string };
          if (typedBody.error === "passport_login_required") {
            const err = new Error(typedBody.message ?? "Please sign in with Passport");
            (err as Error & { code?: string }).code = "passport_login_required";
            throw err;
          }
          throw new Error(typedBody.message ?? "Login failed");
        }
        await res.json().catch(() => ({} as AuthApiResponse));
        setUser(await loadCurrentUser(authUrl));
        return { mfaRequired: false };
      } catch (err) {
        setError(err instanceof Error ? err.message : "Login failed");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authUrl]
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, args: { code?: string; recoveryCode?: string }) => {
      setError(null);
      setIsLoading(true);
      try {
        const res = await fetch(`${authUrl}/auth/mfa/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            mfa_token: mfaToken,
            code: args.code,
            recovery_code: args.recoveryCode,
          }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
          throw new Error(body.message ?? body.error ?? "Verification failed");
        }
        await res.json().catch(() => ({} as AuthApiResponse));
        // The cookie is set; hydrate the in-memory session like login does (no hard reload needed).
        setUser(await loadCurrentUser(authUrl));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authUrl]
  );

  const challengeMfa = useCallback(
    async (mfaToken: string): Promise<string> => {
      const res = await fetch(`${authUrl}/auth/mfa/challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mfa_token: mfaToken }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
        throw new Error(body.message ?? body.error ?? "Could not resend the code");
      }
      const body = (await res.json().catch(() => ({}))) as { mfa_token?: string };
      // The server re-mints a fresh rolling token — the caller swaps it in so the next verify uses it.
      return body.mfa_token ?? mfaToken;
    },
    [authUrl]
  );

  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      setError(null);
      setIsLoading(true);
      try {
        const res = await fetch(`${authUrl}/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          // first/last are the canonical name; display_name is derived server-side (sc-89494).
          body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { message?: string }).message ?? "Registration failed");
        }
        await res.json().catch(() => ({} as AuthApiResponse));
        setUser(await loadCurrentUser(authUrl));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Registration failed");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authUrl]
  );

  const logout = useCallback(async () => {
    await fetch(`${authUrl}/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    setUser(null);
    setError(null);
  }, [authUrl]);

  const refreshToken = useCallback(async () => {
    try {
      const res = await fetch(`${authUrl}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        await res.json().catch(() => ({} as AuthApiResponse));
        setUser(await loadCurrentUser(authUrl));
      } else {
        setUser(null);
      }
    } catch {
      // Network error — leave existing session state alone
    }
  }, [authUrl]);

  useEffect(() => {
    if (!user) return;
    const interval = window.setInterval(() => {
      void refreshToken();
    }, 45 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [user, refreshToken]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    error,
    login,
    logout,
    register,
    loginWithPassport,
    verifyMfa,
    challengeMfa,
  };

  return (
    <AuthConfigContext value={config}>
      <AuthContext value={value}>
        {children}
      </AuthContext>
    </AuthConfigContext>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
